import React, { useEffect, useState } from "react";
import { ActionIcon, Flex, Text, Button, Box } from "@mantine/core";
import axios from "axios";
import toast from "react-hot-toast";
import styled from "styled-components";
import { IconCopy } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import VehicleCard from "./VehicleCard";
import OdometerValueMismatch from "./OdometerValueMismatch";
import DealerMode from "./DealerMode";
import OdometerMismatchModal from "./OdometerMismatchModal";
import OdometerForm from "./OdometerForm";
import NhtsaResults from "./NhtsaResults";
import AddVin from "./AddVin";
import {
  nhtsaMakeIsValid,
  nhtsaTypeIsValid,
  getDataFromNhtsaResults,
} from "./helpers";

const UnverifiedItem = ({
  info,
  additionalReqData,
  fetchUser,
  verificationCode,
  dealerCodeData,
  isVehicleDataEffort = false,
  registerExistingVehicle = false,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [isAdding, setAdding] = useState(false);
  const [override, setOverride] = useState(false);
  const [showReading, setShowReading] = useState(false);
  const [odometerMismatchModalOpen, setOdometerMismatchModalOpen] =
    useState(false);

  useEffect(() => {
    if (verificationCode) {
      if (info.odometer_value) {
        switch (`${info.odometer_value}`.length) {
          case 1:
            setValue(`00000${info.odometer_value}`);
            break;
          case 2:
            setValue(`0000${info.odometer_value}`);
            break;
          case 3:
            setValue(`000${info.odometer_value}`);
            break;
          case 4:
            setValue(`00${info.odometer_value}`);
            break;
          case 5:
            setValue(`0${info.odometer_value}`);
            break;
          default:
            setValue(`${info.odometer_value}`);
            break;
        }
        // setValue(info.odometer_value);
      }
    }
  }, []);

  const nhtsaLookupFailure =
    info?.nhtsa_results?.success === false &&
    info?.nhtsa_results?.response[0]?.Variable === "Error Text"
      ? true
      : null;
  const vinVerified = info?.nhtsa_results?.success;
  const isLegacyVin = info && parseInt(info.year) < 1981;
  const validMake =
    isLegacyVin || nhtsaLookupFailure
      ? true
      : nhtsaMakeIsValid(
          info.nhtsa_results?.response,
          isVehicleDataEffort ? false : true
        );

  const validType =
    isLegacyVin || nhtsaLookupFailure
      ? true
      : nhtsaTypeIsValid(info.nhtsa_results?.response, ["1"]);
  const showEdit = info?.edit_vin === true;
  const showOtherButtons =
    showEdit || (!vinVerified && !isLegacyVin && validMake && validType);

  const reqData = {
    ...additionalReqData,
    ride_challenge_data_point_id: info.id,
    user_interaction_id: info.user_interaction_id,
  };
  function resetVerify() {
    setValue("");
    setShowReading(false);
  }

  function resolveOdometerMismatch(val) {
    setValue(val);
    setOdometerMismatchModalOpen(false);
    onSubmit(val);
  }

  function onSubmit(odometerValueOverride = null) {
    const odometerValue =
      odometerValueOverride !== null ? odometerValueOverride : value;

    if (
      info.odometer_value !== parseInt(odometerValue) &&
      !odometerMismatchModalOpen
    ) {
      setOdometerMismatchModalOpen(true);
      return;
    }

    setLoading(true);

    const nhtsaData = getDataFromNhtsaResults(
      info.nhtsa_results?.response
    ).reduce((acc, cur) => {
      acc[cur.server_key] = cur.value;
      return acc;
    }, {});

    const req = {
      ...reqData,
      ...nhtsaData,
      checksum_results: info.checksum_results,
      nhtsa_results: info.nhtsa_results,
      odometer_value: odometerValue,
      vin: info.vin,
    };

    if (isLegacyVin) {
      req.year = info.year;
      req.model = info.model;
      req.make = info.make;
    }

    axios
      .post(`/ride-challenge/verify-reading/`, req)
      .then(() => {
        toast.success(t("common:verified") + "!");
        setLoading(false);
        fetchUser();
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  if (registerExistingVehicle) {
    return (
      <DealerMode dealerCodeData={dealerCodeData}>
        <Flex gap="xs" align="center" mt="lg">
          <Text
            size="xl"
            fw={600}
            c="white"
            style={{ textTransform: "uppercase" }}
          >
            VIN: {info.vin}
          </Text>
          <ActionIcon
            size="sm"
            color="gray"
            variant="light"
            onClick={() => {
              navigator.clipboard.writeText(info.vin);
              toast.success(t("common:copied") + "!");
            }}
          >
            <IconCopy style={{ width: "70%", height: "70%" }} />
          </ActionIcon>
        </Flex>
        {isLegacyVin && <VehicleCard info={info} />}
        {!isLegacyVin &&
          info.nhtsa_results &&
          info.nhtsa_results.success !== undefined && (
            <NhtsaResults results={info.nhtsa_results} />
          )}
        <Box mt="lg">
          <OdometerValidation
            info={info}
            value={value}
            setValue={setValue}
            onSubmit={() => onSubmit()}
            loading={loading}
            odometerMismatchModalOpen={odometerMismatchModalOpen}
            resolveOdometerMismatch={resolveOdometerMismatch}
            setOdometerMismatchModalOpen={setOdometerMismatchModalOpen}
          />
        </Box>
      </DealerMode>
    );
  }

  if (isAdding) {
    return (
      <DealerMode dealerCodeData={dealerCodeData}>
        <Text c="red" fw={600} align="center">
          {t("microsite:edit_mode")}
        </Text>
        <AddVin
          isOverride={override}
          onSuccess={() => fetchUser()}
          additionalReqData={reqData}
          originalData={info}
          isVehicleDataEffort={isVehicleDataEffort}
        />
        {/* <Divider mt="lg" mb="lg" /> */}
        <Flex justify="center" mt="xl">
          <Button
            c="dark"
            color="gray.1"
            size="xs"
            radius="lg"
            onClick={() => {
              setAdding(false);
              resetVerify();
            }}
          >
            {t("microsite:cancel_changes")}
          </Button>
        </Flex>
      </DealerMode>
    );
  }

  return (
    <DealerMode dealerCodeData={dealerCodeData}>
      <Text mb="xl" c="white" fw={600} align="center">
        {t("microsite:please_confirm_the_following")}:
      </Text>
      <Flex gap="xs" align="center">
        <Text
          size="xl"
          fw={600}
          c="white"
          style={{ textTransform: "uppercase" }}
        >
          VIN: {info.vin}
        </Text>
        <ActionIcon
          size="sm"
          color="gray"
          variant="light"
          onClick={() => {
            navigator.clipboard.writeText(info.vin);
            toast.success(t("common:copied") + "!");
          }}
        >
          <IconCopy style={{ width: "70%", height: "70%" }} />
        </ActionIcon>
      </Flex>
      {verificationCode && (
        <Text size="xl" fw={600} c="white">
          {info.odometer_value} miles
        </Text>
      )}
      {isLegacyVin && <VehicleCard info={info} />}
      {!isLegacyVin &&
        info.nhtsa_results &&
        info.nhtsa_results.success !== undefined && (
          <NhtsaResults results={info.nhtsa_results} />
        )}
      {showReading && (
        <Box mt="lg">
          <OdometerValidation
            info={info}
            value={value}
            setValue={setValue}
            onSubmit={() => onSubmit()}
            loading={loading}
            odometerMismatchModalOpen={odometerMismatchModalOpen}
            resolveOdometerMismatch={resolveOdometerMismatch}
            setOdometerMismatchModalOpen={setOdometerMismatchModalOpen}
          />
        </Box>
      )}
      {!validMake && (
        <Text mt="lg" c="red" fw={600} align="center">
          {t("mixer:vehicle:make_error_message")}
        </Text>
      )}
      {!validType && (
        <Text mt="lg" c="red" fw={600} align="center">
          {t("mixer:vehicle:vehicle_type_error_message")}
        </Text>
      )}
      {(vinVerified || isLegacyVin) &&
        !showReading &&
        validMake &&
        validType && (
          <Button
            fullWidth
            mt="md"
            mb={showOtherButtons ? "xl" : 0}
            onClick={() => {
              setShowReading(true);
            }}
          >
            {t("common:confirm")}
          </Button>
        )}
      {showOtherButtons && (
        <Flex
          justify="center"
          gap="md"
          align="center"
          direction={{ base: "column", xs: "row" }}
          mt="lg"
        >
          {showEdit && (
            <Button
              c="dark"
              size="xs"
              color="gray.1"
              radius="lg"
              onClick={() => {
                setAdding(true);
                setOverride(false);
              }}
            >
              {t("microsite:i_need_to_make_changes")}
            </Button>
          )}
          {!vinVerified && !isLegacyVin && validMake && validType && (
            <>
              <Text fw={600} c="dimmed" size="xs">
                {t("common:or")}
              </Text>
              <Button
                c="dark"
                size="xs"
                color="gray.1"
                radius="lg"
                onClick={() => {
                  setAdding(true);
                  setOverride(true);
                }}
              >
                {t("mixer:vehicle:proceed_anyway")}
              </Button>
            </>
          )}
        </Flex>
      )}
      {!showEdit && (
        <Text mt="lg" c="dimmed" align="center" size="xs">
          {t("mixer:vehicle:editing_unavailable")}
        </Text>
      )}
    </DealerMode>
  );
};

const StyledOdometerForm = styled.div`
  background: var(--mantine-color-dark-8);
  padding: 20px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export default UnverifiedItem;

const OdometerValidation = ({
  info,
  value,
  setValue,
  onSubmit,
  loading,
  odometerMismatchModalOpen,
  resolveOdometerMismatch,
  setOdometerMismatchModalOpen,
}) => {
  const { t } = useTranslation();
  return (
    <div>
      <StyledOdometerForm>
        <OdometerForm value={value} onChange={(e) => setValue(e)} />
        {info.odometer_value && (
          <OdometerValueMismatch
            originalValue={info.odometer_value}
            newValue={`${value}`}
          />
        )}
        <Flex mt="lg">
          <Button onClick={onSubmit} loading={loading} disabled={!value}>
            {t("common:verify")}
          </Button>
        </Flex>
        {odometerMismatchModalOpen && (
          <OdometerMismatchModal
            userValue={info.odometer_value}
            dealerValue={value}
            onClose={() => setOdometerMismatchModalOpen(false)}
            onSubmit={(val) => resolveOdometerMismatch(val)}
          />
        )}
        {loading && (
          <Text mt="lg" size="xs" fw={600} c="dimmed" align="center">
            {t("mixer:vehicle:nhtsa_loading_message")}
          </Text>
        )}
      </StyledOdometerForm>
    </div>
  );
};
